import { useMutation, useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import {
  tenantManagementApi,
  type RegisterDomainResponse,
} from '../../../api/tenantManagement';
import { validateDomain } from '../../../schemas/domain.schema';

// Re-export for convenience
export { domainSchema, validateDomain } from '../../../schemas/domain.schema';
export type { DomainValidationResult } from '../../../schemas/domain.schema';

interface CheckDomainExistsParams {
  domain: string;
  tenantId: string;
}

interface CheckDomainExistsResult {
  exists: boolean;
  belongsToCurrentTenant: boolean;
}

/**
 * Hook to check if a domain already exists in domain-mappings collection
 */
export const useCheckDomainExists = () => {
  return useMutation({
    mutationFn: async ({
      domain,
      tenantId,
    }: CheckDomainExistsParams): Promise<CheckDomainExistsResult> => {
      const mappingRef = doc(db, 'domain-mappings', domain);
      const mappingDoc = await getDoc(mappingRef);

      if (!mappingDoc.exists()) {
        return { exists: false, belongsToCurrentTenant: false };
      }

      const data = mappingDoc.data();
      return {
        exists: true,
        belongsToCurrentTenant: data.tenantId === tenantId,
      };
    },
  });
};

interface ChangeDomainParams {
  oldDomain: string;
  newDomain: string;
  organizationId: string;
}

interface ChangeDomainResult {
  success: boolean;
  domainRegistrationResult?: RegisterDomainResponse;
}

/**
 * Hook to change domain with proper flow:
 * 1. First validate domain format
 * 2. Check if new domain already exists and belongs to another tenant
 * 3. Register new domain in Firebase Auth and Hosting
 * 4. Update tenant with new domain
 *
 * Note: Domain mapping creation/deletion is handled by backend
 */
export const useChangeDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      oldDomain,
      newDomain,
      organizationId,
    }: ChangeDomainParams): Promise<ChangeDomainResult> => {
      // Step 1: Validate domain format
      const validation = validateDomain(newDomain);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Step 2: Check if new domain already exists and belongs to another tenant
      if (newDomain !== oldDomain) {
        const newMappingRef = doc(db, 'domain-mappings', newDomain);
        const existingDoc = await getDoc(newMappingRef);

        if (existingDoc.exists()) {
          const existingData = existingDoc.data();
          if (existingData.tenantId !== organizationId) {
            throw new Error(`Domain "${newDomain}" is already registered to another organization.`);
          }
        }
      }

      // Step 3: Register new domain in Firebase Auth and Hosting FIRST
      // This is the critical step - if this fails, we don't proceed with any other changes
      let domainRegistrationResult;
      try {
        domainRegistrationResult = await tenantManagementApi.registerDomain({
          organisation_id: organizationId,
          domain: newDomain,
        });
        console.log('Domain registration result:', domainRegistrationResult.data);

        // Backend returns 207 with success:false when Firebase APIs fail — must check explicitly
        if (!domainRegistrationResult.data?.success) {
          const authError = domainRegistrationResult.data?.results?.authentication?.error;
          const hostingError = domainRegistrationResult.data?.results?.hosting?.error;
          const errorDetails = authError || hostingError || 'Domain registration failed';
          throw new Error(errorDetails);
        }
      } catch (domainError) {
        console.error('Domain registration error:', domainError);
        throw new Error(
          `Failed to register domain: ${domainError instanceof Error ? domainError.message : 'Unknown error'}`
        );
      }

      // Step 4: Only after successful domain registration, delete old domain mapping
      // Note: Backend now handles domain mapping deletion
      // if (oldDomain && oldDomain !== newDomain) {
      //   const oldMappingRef = doc(db, 'domain-mappings', oldDomain);
      //   await deleteDoc(oldMappingRef);
      //   console.log('Deleted old domain mapping:', oldDomain);
      // }

      // Step 5: Update tenant with new domain
      const domainUpdate = { domain: { primary: newDomain, aliases: [] as string[] } };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- UpdateTenantRequest type doesn't include domain field
      await tenantManagementApi.updateTenant(organizationId, domainUpdate as any);
      console.log('Updated tenant with new domain:', newDomain);

      // Step 6: Create new domain mapping
      // Note: Backend now handles domain mapping creation
      // const newMappingRef = doc(db, 'domain-mappings', newDomain);
      // await setDoc(newMappingRef, {
      //   domain: newDomain,
      //   tenantId: organizationId,
      // });
      // console.log('Created new domain mapping:', newDomain);

      return {
        success: true,
        domainRegistrationResult: domainRegistrationResult?.data,
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.organizationId] });
      if (variables.oldDomain) {
        queryClient.invalidateQueries({ queryKey: ['domain-mapping', variables.oldDomain] });
      }
      queryClient.invalidateQueries({ queryKey: ['domain-mapping', variables.newDomain] });
    },
  });
};
